// import { BLFormItem } from 'bldx-core';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import classNames from 'classnames';
import { debounce } from 'underscore';

import { createNewEvent, editSessionAgenda, loadTemplates, removeInCreationProgressSession, setEventName, updateRegistrationSteps } from '../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../store/types';
import { CreateSession, SessionTypesEnum } from '../../../../types/working-model';
import TextInput, { Validation } from '../../../general-ui/text-input/text';
import Agenda from '../agenda/agenda';
import { CheckEventName, CreateEvent, UpdateRegistrationSteps } from '../../../../connection/create-event';
import Icon, { COLORS, ICONS } from '../../../general-ui/icon';
import { selectedTemplateKey } from '../templates/template';
import NavigationDropdown from '../../../general-ui/dropdown/navigation-dropdown';
import WaitingIndicator from '../../../general-ui/waiting-indicator/waiting-indicator';
import './info.scss';
import EventHeader from '../../header/event-header';
import BlueRight from '../../../../images/blue-lines-right.svg';
import BlueLeft from '../../../../images/blue-lines-left.svg';
import ModalComponent from '../../../general-ui/modal/modal';
import useRequiredFields from '../../../../utils/use-required-fields';
import { PATHNAMES } from "../../admin";
import { GetDefaultRegistrationSteps } from '../../../../store/utils/create-event';
import { showAlert } from '../../../general-ui/alert/alert-service';
import { addAdminEvent } from '../../../../store/actions/admin/events';
// import Homepage from '../homepage/homepage';

// function onlyWithDates(session: CreateSession) {
// 	return !!session.start_timestamp;
// }

// function datesFromTimestamps(session: CreateSession): Date {
// 	return new Date(session.start_timestamp!);
// }

function formatTime(timestamp: number): string {
	const r = new Date(timestamp);
	const h = r.getHours();
	const res = r.toLocaleTimeString().split(' ')[0].slice(0, -3);
	const ampm = h >= 12 ? " PM" : " AM";

	return res + ampm;
}
interface SessionsByDate {
	[key: string]: CreateSession[];
}

export default function Info(): JSX.Element {
	const dispatch = useDispatch();
	const history = useHistory();
	const {
		CreateEventReducer: {
			newEventRequest
		},
		AuthReducer: {
			user,
			token,
		},
	} = useSelector((state: EventsState) => state);
	const [selectedDate, setSelectedDate] = useState("");
	const [registrationToggleIsOpen, setRegistrationToggleIsOpen] = useState(false);
	const [registrationIsOn, setRegistrationIsOn] = useState(false);
	// create landing page default for single session events here
	const [addSingleSessionHomepage, setAddSingleSessionHomepage] = useState(true);
	const [displayAddHomepage, setDisplayAddHomepage] = useState(false);
	const [loading, setLoading] = useState(false);
	const [eventNameError, setEventNameError] = useState('');

	const abortNameCheck = useRef<AbortController | null>(null);

	const {
		requiredFields,
		hasCompletedRequiredFields,
		updateRequiredField,
	} = useRequiredFields(['name'], {
		name: (string: string) => {
			const isValid = !!string.trim();
			if (!isValid) setEventNameError('Invalid event name');
			return isValid;
		}
	});

	// if page is reloaded, then we cannot add sessions without the newEventRequest field
	// 1. fetch templates
	// 2. repopulate the store with a newEventRequest
	useEffect(() => {
		const selectedTemplateString = localStorage.getItem(selectedTemplateKey);
		if (!newEventRequest && selectedTemplateString) {
			const selectedTemplate = JSON.parse(selectedTemplateString);
			batch(() => {
				dispatch(loadTemplates(user!.active_channel, token!));
				dispatch(createNewEvent(user!.active_channel, selectedTemplate));
			});
		} else if (!newEventRequest && !selectedTemplateString) {
			history.push(PATHNAMES.CreateEvent);
		}

		return () => {
			localStorage.removeItem(selectedTemplateKey);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const openRegistrationToggle = () => setRegistrationToggleIsOpen(true);
	const closeRegistrationToggle = () => {
		setAddSingleSessionHomepage(true);
		setDisplayAddHomepage(false);
		setRegistrationToggleIsOpen(false);
	};

	const toggleRegistration = ({ target }: ChangeEvent<HTMLInputElement>) => {
		//inputs have strings as value, change it to a boolean before setting the state 
		const stringToBool = target.value !== "false";
		setRegistrationIsOn(stringToBool);
	};

	const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		updateRequiredField('name', val);
		dispatch(setEventName(val));
	}, [dispatch, updateRequiredField]);

	const handleNameInputBlur = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
		updateRequiredField('name', value);
	}, [updateRequiredField]);

	const checkName = useCallback(async (eventName: string) => {
		try {
			//use an abort controller here if someone types super fast, we don't want the 
			//fetch requests to arrive out of order and mess things up. 
			//so if the previous request is still in flight, abort the request and send a new one.
			if (abortNameCheck.current) abortNameCheck.current.abort();

			// passing 0 here for eventId because the event isnt created yet
			const [controller, checkEventNamePromise] = CheckEventName(token!, user!.active_channel, eventName.trim(), 0);
			abortNameCheck.current = controller;

			checkEventNamePromise.then(({ exists }) => {
				if (exists) {
					setEventNameError('Event name already used for this Channel');
				} else {
					setEventNameError('');
				}
			}).catch(e => {
				console.error(e);
			});
		} catch (e) {
			console.error(e);
			showAlert({
				message: "Error Checking Event Name",
				description: "We were unable to check that event name. Please try again",
				type: "error"
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	const debounced = useRef(debounce((value: string, cb: (value: string) => void) => {
		cb(value);
	}, 300)).current;

	const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		debounced((e.target as HTMLInputElement).value, (val) => {
			checkName(val);
		});
	}, [checkName, debounced]);

	const firstKeyWithSessions = useCallback((dateObj: SessionsByDate) => {
		for (const key in dateObj) {
			if (dateObj[key].length > 0) {
				return key;
			}
		}

		return Object.keys(dateObj)[0];
	}, []);

	const sessionsByDate: SessionsByDate | null = useMemo(() => {
		if (newEventRequest?.sessions) {
			const dateObj: SessionsByDate = { "On demand": [] };

			for (let i = 0; i < newEventRequest.sessions.length; ++i) {
				const session = newEventRequest.sessions[i];
				if (session.session_type === SessionTypesEnum.onDemand) {
					dateObj['On demand'].push(session);
				} else if (session.session_type === SessionTypesEnum.broadcast) {
					const date = new Date(session.start_timestamp!);
					const dateStr = date.toLocaleDateString(navigator.language, { weekday: 'short', month: 'short', day: '2-digit' });

					if (dateObj[dateStr]) {
						dateObj[dateStr].push(session);
					} else {
						dateObj[dateStr] = [session];
					}
				}
			}


			setSelectedDate(firstKeyWithSessions(dateObj));
			return dateObj;
		} else {
			return null;
		}
	}, [newEventRequest?.sessions, firstKeyWithSessions]);

	function selectDate(date: string) {
		return () => setSelectedDate(date);
	}

	const isValid = useMemo(() => {
		return newEventRequest && newEventRequest.name && newEventRequest?.sessions.length > 0 && hasCompletedRequiredFields(newEventRequest) && !eventNameError;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newEventRequest, eventNameError]);

	function skipHomepage() {
		if (newEventRequest) {
			const newEvent = {
				...newEventRequest,
				registration_on: registrationIsOn
			};
			CreateEvent(newEvent, token!).then(result => {
				if (result.error) {
					console.error(result.error);
					alert("Error creating event");
				} else {
					dispatch(addAdminEvent(result));
					history.push(PATHNAMES.Event.EventLink(result.uuid));
				}
			}).catch(e => {
				console.error(e);
				alert("Error creating event");
			});
		}
	}

	async function finish() {
		// exit if event name is invalid
		if (newEventRequest && !hasCompletedRequiredFields(newEventRequest)) return;

		setLoading(true);
		// if one session, see if they want a homepage
		// if there is more than one session, a homepage is required.
		if (newEventRequest && newEventRequest.sessions.length > 1) {
			const newEvent = {
				...newEventRequest,
				homepage: true,
				registration_on: registrationIsOn
			};
			try {
				const result = await CreateEvent(newEvent, token!);
				if (result.error) {
					console.error(result.error);
					alert("Error creating event");
				} else {
					dispatch(addAdminEvent(result));
					// load default registration steps if registration is on for new event
					if (registrationIsOn) {
						const defaultSteps = GetDefaultRegistrationSteps();

						dispatch(updateRegistrationSteps(defaultSteps));
						UpdateRegistrationSteps(token!, result.uuid, defaultSteps);
					}
					history.push(PATHNAMES.Event.LandingLink(result.uuid));
				}
			} catch (e) {
				console.error(e);
				alert("Error creating event");
			}
			return;
			// if singleSession, check if user want a homepage
		} else if (!displayAddHomepage) {
			setDisplayAddHomepage(true);
		} else if (addSingleSessionHomepage) {
			if (newEventRequest) {
				const newEvent = {
					...newEventRequest,
					homepage: true,
					registration_on: registrationIsOn
				};
				try {
					const result = await CreateEvent(newEvent, token!);
					if (result.error) {
						console.error(result.error);
						alert("Error creating event");
					} else {
						dispatch(addAdminEvent(result));
						// load default registration steps if registration is on for new event
						if (registrationIsOn) {
							const defaultSteps = GetDefaultRegistrationSteps();

							dispatch(updateRegistrationSteps(defaultSteps));
							UpdateRegistrationSteps(token!, result.uuid, defaultSteps);
						}
						history.push(PATHNAMES.Event.LandingLink(result.uuid));
					}
				} catch (e) {
					console.error(e);
					alert("Error creating event");
				}
				return;
			}
		} else {
			skipHomepage();
		}
		setLoading(false);
	}

	const handleDeleteSessionAgenda = (uid: string) => () => {
		dispatch(removeInCreationProgressSession(uid));
	};

	const handleEditSessionAgenda = (uid: string) => () => {
		const session = newEventRequest?.sessions?.find((session: CreateSession) => session?.uid === uid);
		if (session) {
			// set it in redux and use those values insead
			dispatch(editSessionAgenda(uid));
		} else {
			dispatch(editSessionAgenda(null));
		}
	};

	const renderDropdowns = (uid: string) => (
		<div className="session-menu">
			<NavigationDropdown
				// note: we are hardcoding the width in class session-menu-dropdown inside info.scss because safari
				// is cutting off the dropdown width. If we add more buttons inside the dropdown, we need to
				// make sure the width is changed accordingly
				navdropClassName="session-menu-dropdown"
				title={<Icon name={ICONS.THREE_DOTS_VERTICAL} size={24} color={COLORS.DEFAULT_GRAY} />}
				isArrow={false}
			>
				<button onClick={handleEditSessionAgenda(uid)}>Edit Session</button>
				<button onClick={handleDeleteSessionAgenda(uid)}>Remove Session</button>
			</NavigationDropdown>
		</div>
	);

	const renderSessionTypeText = (session: CreateSession) => {
		if (session.session_type === SessionTypesEnum.onDemand) {
			return <span>&nbsp;&nbsp;ON DEMAND</span>;
		} else if (session.session_type === SessionTypesEnum.broadcast) {
			return <span>&nbsp;&nbsp;BROADCAST</span>;
		}
	};

	const renderRegistrationOptions = (isSingleSession: boolean | null) => {
		// if more than one session, dont ask about making a homepage
		return !isSingleSession ? (
			<AddRegistration {...{ toggleRegistration, registrationIsOn }} />
		) : (
			displayAddHomepage ? (
				<AddHomepage {...{ addSingleSessionHomepage, setAddSingleSessionHomepage }} />
			) : (
				<AddRegistration {...{ toggleRegistration, registrationIsOn }} />
			)
		);
	};

	const validName = (requiredFields.name === Validation.normal && !eventNameError) ? Validation.normal : Validation.error;
	const isSingleSession = newEventRequest && newEventRequest.sessions.length === 1;
	return (
		<div className="create-event-info-page-wrapper">
			<div>
				<EventHeader
					hideCreateUI
					fullWidth
					showLogo
					linkText="Back to templates"
					linkTo={PATHNAMES.CreateEvent}
				/>
			</div>
			<div className="wrapper create-event-info-wrapper" style={{ backgroundImage: `url(${BlueLeft}), url(), url(${BlueRight})` }}>
				<div className="container create-event-info">
					<div className="block">
						<h2>Create a new event</h2>
						<p>Get started by adding your basic event information and agenda.</p>
					</div>

					<div className="block create-event-name-container">
						<h3>Choose a name</h3>
						<p>The event name will help you identify it in the event list.</p>
						<div className="create-event-name-input-wrapper">
							<TextInput
								onChange={handleNameChange}
								placeholder="Name of your event"
								onBlur={handleNameInputBlur}
								valid={validName}
								onKeyUp={handleKeyUp}
								errorMessage={eventNameError}
							/>
						</div>
					</div>

					<div className="block create-event-agenda-container">
						<h3>Agenda</h3>
						<p>An agenda for the event can help you significantly in terms of planning and organizing.</p>
						{!!sessionsByDate && (
							<div className="create-sessions-container">
								{Object.keys(sessionsByDate).length === 1 ? (
									<div className="create-sessions-list">
										{sessionsByDate['On demand']?.map((session: CreateSession) => (
											<div key={session.uid} className="create-session">
												<div className="session-top">
													<h5><span>{session.title}</span></h5>
													{renderDropdowns(session.uid || '')}
												</div>
												<div className="session-tags">
													<div className="session-tracks">
														<div>
															<Icon name={ICONS.VIEWERS_EYE} size={14} color={COLORS.DEFAULT_GRAY} />
															{renderSessionTypeText(session)}
														</div>
														{session.tracks?.map((track: string, index: number) => (
															<div key={index}>{track.toUpperCase()}</div>
														))}
													</div>
													<div className="session-langs">
														{session.languages.length > 5 ? (
															session.languages.slice(0, 5)?.map((lang: string, index: number) => (
																<div key={index} className="languages">{lang.toLocaleUpperCase()}</div>
															))
														) : (
															session.languages?.map((lang: string, index: number) => (
																<div key={index} className="languages">{lang.toLocaleUpperCase()}</div>
															))
														)}
														{session.languages.length > 5 ? <div>+ {session.languages.length - 5} more</div> : ""}
													</div>
												</div>
											</div>
										)
										)}
									</div>
								) : (
									<>
										<div className="create-sessions-header">
											{Object.keys(sessionsByDate).map((key: string) => {
												if (sessionsByDate[key].length > 0) {
													return <button className={classNames({ active: selectedDate === key })} key={key} onClick={selectDate(key)}>{key}</button>;
												} else {
													return <></>;
												}
											})}
										</div>
										<div className="create-sessions-list">
											{sessionsByDate[selectedDate]?.map((session: CreateSession) => (
												<div key={session.uid} className="create-session">
													<div className="session-top">
														<div>
															<span>{session.start_timestamp ? formatTime(session.start_timestamp) + " — " : ""} {session.end_timestamp ? formatTime(session.end_timestamp) : ""}</span>
															<span><h4>{session.title}</h4></span>
														</div>
														{renderDropdowns(session.uid || '')}
													</div>
													<div className="session-tags">
														<div className="session-tracks">
															<div>
																<Icon name={ICONS.VIEWERS_EYE} size={14} color={COLORS.DEFAULT_GRAY} />
																{renderSessionTypeText(session)}
															</div>
															{session.tracks?.map((track: string) => (
																<div key={track}>{track.toUpperCase()}</div>
															))}
														</div>
														<div className="session-langs">
															{session.languages.length > 5 ? (
																session.languages.slice(0, 5)?.map((lang: string) => (
																	<div key={lang} className="languages">{lang.toLocaleUpperCase()}</div>
																))
															) : (
																session.languages?.map((lang: string) => (
																	<div key={lang} className="languages">{lang.toLocaleUpperCase()}</div>
																))
															)}
															{session.languages.length > 5 ? <div>+ {session.languages.length - 5} more</div> : ""}
														</div>
													</div>

												</div>
											))}
										</div>
									</>
								)}
							</div>
						)}
						<Agenda />
					</div>

					<button className="primary create-event-continue" disabled={!isValid} onClick={openRegistrationToggle}>Continue <Icon name={ICONS.ARROW_RIGHT} size={14} color={COLORS.GRAY} /></button>
				</div>
			</div>
			<ModalComponent
				title={displayAddHomepage ? "Add landing page" : "Set event availability"}
				subtitle={displayAddHomepage ? "A landing page allows you to provide a preview and market your event to guests before they register." : undefined}
				open={registrationToggleIsOpen}
				onRequestClose={closeRegistrationToggle}
				cancellable={false}
				closeable={false}
				className="registration-toggle-modal"
				footer={<>
					<button onClick={closeRegistrationToggle}>Cancel</button>
					{loading
						? <button className="primary"><WaitingIndicator /></button>
						: <button className="primary" onClick={finish}>{(displayAddHomepage || !isSingleSession) ? 'Confirm' : 'Next'}</button>
					}
				</>}
			>
				{renderRegistrationOptions(isSingleSession)}
			</ModalComponent>
		</div>
	);
}

interface IAddRegistrationProps {
	toggleRegistration: (e: React.ChangeEvent<HTMLInputElement>) => void;
	registrationIsOn: boolean;
}

const AddRegistration: React.FC<IAddRegistrationProps> = ({ toggleRegistration, registrationIsOn }) => {
	return (
		<>
			<input type="radio" name="registration-toggle" value="false" id="registration-off" onChange={toggleRegistration} checked={!registrationIsOn} />
			<label htmlFor="registration-off">
				<div className="radio-circle" />
				<div>
					<h6>Open</h6>
					<span>Anyone can see the event. No registration needed.</span>
				</div>
			</label>

			<input type="radio" name="registration-toggle" value="true" id="registration-on" onChange={toggleRegistration} checked={registrationIsOn} />
			<label htmlFor="registration-on">
				<div className="radio-circle" />
				<div>
					<h6>Registration required</h6>
					<span>Only registered users will see event agenda and other pages</span>
				</div>
			</label>
		</>
	);
};

interface IAddHomepageProps {
	addSingleSessionHomepage: boolean;
	setAddSingleSessionHomepage: (b: boolean) => void;
}

export const AddHomepage: React.FC<IAddHomepageProps> = ({ addSingleSessionHomepage, setAddSingleSessionHomepage }) => {
	return (
		<div className="add-homepage-container">
			<input type="radio" name="homepage-toggle" value="true" id="homepage-on" onChange={() => setAddSingleSessionHomepage(true)} checked={addSingleSessionHomepage} />
			<label htmlFor="homepage-on">
				<div className="radio-circle" />
				<div>
					<h6>Yes</h6>
					<span></span>
				</div>
			</label>

			<input type="radio" name="homepage-toggle" value="false" id="homepage-off" onChange={() => setAddSingleSessionHomepage(false)} checked={!addSingleSessionHomepage} />
			<label htmlFor="homepage-off">
				<div className="radio-circle" />
				<div>
					<h6>No</h6>
					<span></span>
				</div>
			</label>
		</div>
	);
};
